import { useContext, useEffect, useState } from 'react';

import { API_URL } from '../../config';

import { PageLayout } from '../../components/page-layout';
import { LoadingContainer } from '../../components/loading-container';
import { InputBox } from '../../components/input-box';
import { ErrorMessage } from '../../components/error-message';
import {
    BACK_TO_START,
    ENTERED_CODE_IS_NOT_CORRECT_NO_MORE_ATTEMPTS,
    ENTER_CODE,
    ENTER_SMS_CODE,
    FAILED_TO_LOAD_ONE_TIME_PASSWORD,
    FAILED_TO_VERIFY_SMS_CODE,
    VERIFY_IDENTITY_DESCRIPTION,
} from '../../translator/phrase-keys';
import { LanguageContext } from '../../components/language-settings';

export function VerifyView({changeView}) {
    const [id, setId] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [code, setCode] = useState("");
    const [codeValid, setCodeValid] = useState(false);
    const [noAttemptsLeft, setNoAttemptsLeft] = useState(false);

    const {translator} = useContext(LanguageContext);

    useEffect(() => {
        if (passwordHasAlreadyBeenReset()) {
            changeView("reset");
            return;
        }

        const [loaded, idFromStorage, expires] = loadLocalStorageData();
        if (!loaded) {
            changeView("identity");
            return;
        }

        if (smsCodeHasExpired(expires)) {
            changeView("timeout");
            return;
        }

        const intervalId = setInterval(() => checkIfSmsCodeHasExpired(expires, intervalId), 1000);
        setId(idFromStorage);

        return () => clearInterval(intervalId);
    }, []);

    function checkIfSmsCodeHasExpired(expires, intervalId) {
        if (smsCodeHasExpired(expires)) {
            clearInterval(intervalId);
            changeView("timeout");
        }
    }

    function updateCode(value) {
        const codeValid = value.length === 6;
        setCode(value);
        setCodeValid(codeValid);
    }

    async function verifySmsCode() {
        setLoading(true);

        const [verified, password, verificationError] = await verifySmsCodeViaApi(id, code);
        if (!verified) {
            setLoading(false);
            setError(verificationError);
            setNoAttemptsLeft(
                verificationError === ENTERED_CODE_IS_NOT_CORRECT_NO_MORE_ATTEMPTS ||
                verificationError === FAILED_TO_LOAD_ONE_TIME_PASSWORD
            );
            return;
        }

        sessionStorage.setItem("temporary_password", password);

        changeView("reset");
    }

    function backToStart() {
        sessionStorage.removeItem("id");
        sessionStorage.removeItem("expires");
        sessionStorage.removeItem("temporary_password");
        
        changeView("identity");
    }

    return (
        <PageLayout
            image="svg/verify.svg"
            progress="2"
            title={translator.translate(ENTER_SMS_CODE)}
            description={translator.translate(VERIFY_IDENTITY_DESCRIPTION)}
        >
            <LoadingContainer loading={loading}>
                <InputBox
                    value={code}
                    onValueChange={value => updateCode(value)}
                    onButtonClicked={verifySmsCode}
                    placeholder={translator.translate(ENTER_CODE)}
                    maxLength="6"
                    isPassword="true"
                    focusOnInit="true"
                    disabled={!codeValid || noAttemptsLeft}
                />

                {error && <ErrorMessage text={translator.translate(error)} />}

                <span
                    className="page-link"
                    onClick={backToStart}
                >
                    {translator.translate(BACK_TO_START)}
                </span>
            </LoadingContainer>
        </PageLayout>
    );
}

async function verifySmsCodeViaApi(id, code) {
    try {
        const response = await fetch(`${API_URL}/api/reset/verify-code`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: id,
                code: code,
            }),
        });

        if (!response.ok) {
            if (response.status === 400) {
                const error_message = await response.text();
                return [false, null, error_message];
            }

            if (response.status === 429) {
                const error_message = await response.text();
                return [false, null, null, error_message];
            }

            return [false, null, FAILED_TO_VERIFY_SMS_CODE];
        }

        const body = await response.json();

        return [true, body.password, null];
    }
    catch {
        return [false, null, FAILED_TO_VERIFY_SMS_CODE];
    }
}

function passwordHasAlreadyBeenReset() {
    const password = sessionStorage.getItem("temporary_password");
    if (password)
        return true;

    return false;
}

function loadLocalStorageData() {
    const id = sessionStorage.getItem("id");
    if (!id)
        return [false, "", null];

    const expires = sessionStorage.getItem("expires");
    if (!expires)
        return [false, "", null];

    return [true, id, expires];
}

function smsCodeHasExpired(expires) {
    if (!expires)
        return true;

    return Date.now() > parseInt(expires);
}